import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Sentinel value written to localStorage to trigger cross-tab logout
const LOGOUT_EVENT_KEY = 'hisab_logout_event';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  // Multi-tab logout sync: when another tab clears auth or writes the logout sentinel,
  // this tab logs out immediately.
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Token was removed in another tab (401 handler or explicit logout)
      if (e.key === 'token' && !e.newValue && user) {
        setUser(null);
        window.location.href = '/login';
      }
      // Explicit logout sentinel from another tab
      if (e.key === LOGOUT_EVENT_KEY && e.newValue) {
        localStorage.removeItem(LOGOUT_EVENT_KEY);
        if (user) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          setUser(null);
          window.location.href = '/login';
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user]);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        const response = await authAPI.getProfile();
        setUser(response.data.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.data.user));
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        setUser(null);
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });

    // Check if 2FA is required
    if (response.data.requires2fa) {
      return {
        requires2fa: true,
        challengeToken: response.data.data.challengeToken,
        expiresAt: response.data.data.expiresAt,
      };
    }

    const { user, token, refreshToken } = response.data.data;

    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(user));

    setUser(user);
    return { user };
  };

  const verify2faLogin = async (challengeToken, code, isRecoveryCode = false) => {
    const payload = {
      challengeToken,
      ...(isRecoveryCode ? { recoveryCode: code } : { totpCode: code }),
    };

    const response = await authAPI.verify2faLogin(payload);
    const { user, token, refreshToken } = response.data.data;

    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(user));

    setUser(user);

    return {
      user,
      warning: response.data.warning,
    };
  };

  const signup = async (data) => {
    const response = await authAPI.signup(data);
    const { user, token, refreshToken } = response.data.data;

    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(user));

    setUser(user);
    return user;
  };

  const logout = async () => {
    // Server-side token revocation (best effort)
    try {
      await authAPI.logout();
    } catch (e) {
      // Continue with local cleanup even if server call fails
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    // Signal other tabs to logout via storage event
    localStorage.setItem(LOGOUT_EVENT_KEY, Date.now().toString());
    localStorage.removeItem(LOGOUT_EVENT_KEY);
    setUser(null);
  };

  const updateUser = async (data) => {
    const response = await authAPI.updateProfile(data);
    const updatedUser = response.data.data.user;
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    return updatedUser;
  };

  const value = {
    user,
    loading,
    login,
    verify2faLogin,
    signup,
    logout,
    updateUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
